import React, { useState } from "react";
import { Button, Input, Form, message, Row, Col, Card } from "antd";
import { MailOutlined, UserOutlined } from "@ant-design/icons";
import Turnstile from "react-turnstile";

const { TextArea } = Input;

const API_URL = process.env.REACT_APP_API_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  message?: string;
}

export default function LeadForm() {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0); // For resetting Turnstile

  const onFinish = async (values: FormData) => {
    if (!API_URL) {
      message.error("Contact form is not configured. Please try again later.");
      return;
    }

    // Check if CAPTCHA is required and token is available
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      message.error("Please complete the security verification.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          message: values.message || "",
          captchaToken: captchaToken,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        message.success("Thank you! Your message has been sent. We'll be in touch soon.");
        form.resetFields();
        setCaptchaToken(null);
        // Reset the Turnstile widget
        setTurnstileKey(prev => prev + 1);
      } else {
        console.error("Contact form error:", result);
        if (result.error === "invalid_email") {
          message.error("Please enter a valid email address.");
        } else if (result.error === "captcha_failed") {
          message.error("Security verification failed. Please complete the CAPTCHA again.");
          // Reset the Turnstile widget on captcha failure
          setTurnstileKey(prev => prev + 1);
          setCaptchaToken(null);
        } else {
          message.error("Something went wrong. Please try again or contact us directly.");
        }
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      message.error("Unable to send message. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card 
      title="Get in Touch" 
      style={{ maxWidth: 600, margin: "0 auto" }}
      className="lead-form-card"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        size="large"
        requiredMark={false}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[
                { required: true, message: "Please enter your first name" },
                { min: 2, message: "First name must be at least 2 characters" }
              ]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="Please enter your first name" 
                disabled={isSubmitting}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[
                { required: true, message: "Please enter your last name" },
                { min: 2, message: "Last name must be at least 2 characters" }
              ]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="Please enter your last name" 
                disabled={isSubmitting}
              />
            </Form.Item>
          </Col>
        </Row>
        
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Please enter your email" },
            { type: "email", message: "Please enter a valid email" }
          ]}
        >
          <Input 
            prefix={<MailOutlined />} 
            placeholder="Please enter your email" 
            disabled={isSubmitting}
          />
        </Form.Item>
        
        <Form.Item
          name="message"
          label="Message"
          rules={[
            { max: 1000, message: "Message cannot exceed 1000 characters" }
          ]}
        >
          <TextArea
            placeholder="Tell us how we can help you..."
            rows={4}
            disabled={isSubmitting}
            showCount
            maxLength={1000}
          />
        </Form.Item>

        {/* Turnstile CAPTCHA */}
        {TURNSTILE_SITE_KEY && (
          <Form.Item style={{ textAlign: "center", marginBottom: 24 }}>
            <Turnstile
              key={turnstileKey}
              sitekey={TURNSTILE_SITE_KEY}
              onVerify={(token) => setCaptchaToken(token)}
              onError={() => {
                setCaptchaToken(null);
                message.error("Security verification failed. Please try again.");
              }}
              onExpire={() => {
                setCaptchaToken(null);
                message.warning("Security verification expired. Please complete it again.");
              }}
            />
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0, textAlign: "center" }}>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={isSubmitting}
            size="large"
            style={{ minWidth: 120 }}
          >
            {isSubmitting ? "Sending..." : "Send Message"}
          </Button>
        </Form.Item>
      </Form>
      
      <div style={{ marginTop: 24, textAlign: "center", fontSize: "14px", color: "#666" }}>
        <p>
          You can also reach us directly at{" "}
          <a href="tel:+17067508906" style={{ color: "#1890ff" }}>
            (706) 750-8906
          </a>
        </p>
      </div>
    </Card>
  );
}